import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Lock, Fingerprint, Server, Eye, Users, Mail, Cookie, KeyRound } from "lucide-react";

export default function Seguranca() {
  return (
    <div className="min-h-screen bg-cream-100" data-testid="seguranca-page">
      {/* Hero */}
      <section className="relative bg-forest-900 text-cream-100 noise-overlay">
        <div className="absolute -top-10 -right-10 w-96 h-96 rounded-full bg-copaiba-500/10 blur-3xl" aria-hidden />
        <div className="container-page py-20 relative">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-copaiba-300 mb-4">
            <ShieldCheck className="w-4 h-4" /> Segurança & Privacidade
          </div>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-tight max-w-3xl">
            Seu acesso ao site da COOMFLONA é <span className="text-copaiba-300">seguro</span> e protegido.
          </h1>
          <p className="mt-6 text-cream-100/80 max-w-2xl leading-relaxed">
            Aqui explicamos, de forma simples, as camadas de segurança que protegem cooperados, visitantes, secretaria e
            todos os dados que circulam neste site.
          </p>

          {/* Badges */}
          <div className="mt-10 flex flex-wrap gap-3">
            <BadgeBig icon={<ShieldCheck className="w-6 h-6 text-copaiba-300" />} title="HTTPS ativo" sub="Conexão criptografada de ponta a ponta" />
            <BadgeBig icon={<KeyRound className="w-6 h-6 text-copaiba-300" />} title="TLS 1.3" sub="Padrão atual de criptografia" />
            <BadgeBig icon={<Lock className="w-6 h-6 text-copaiba-300" />} title="LGPD" sub="Dados pessoais protegidos" />
          </div>
        </div>
      </section>

      {/* Camadas de proteção */}
      <section className="container-page py-16">
        <div className="max-w-5xl">
          <div className="text-xs uppercase tracking-[0.22em] text-forest-700 mb-3">Como o site é protegido</div>
          <h2 className="font-display text-3xl md:text-4xl text-forest-900 mb-10">
            Sete camadas de segurança trabalhando para você
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            <Card
              icon={<ShieldCheck className="w-6 h-6 text-copaiba-700" />}
              title="1. HTTPS obrigatório em todo o site"
              body="Toda comunicação entre o seu navegador e o servidor da COOMFLONA é criptografada com TLS 1.3 (o padrão atual mais forte). Ninguém consegue interceptar o que você envia — nem os dados do formulário do Setor Social, nem sua candidatura ao PSS, nem senhas de acesso à Secretaria Virtual."
            />
            <Card
              icon={<Lock className="w-6 h-6 text-copaiba-700" />}
              title="2. Senhas com hash bcrypt"
              body="Nenhuma senha é armazenada em texto puro. Todo login (Admin, Secretaria, Setor Social) usa hashing bcrypt — mesmo que alguém acessasse o banco, as senhas continuariam ilegíveis."
            />
            <Card
              icon={<Fingerprint className="w-6 h-6 text-copaiba-700" />}
              title="3. Tokens JWT com expiração"
              body="Sessões da Secretaria Virtual e do Admin usam tokens JWT assinados que expiram automaticamente. Cada requisição é validada — não há acesso permanente sem autenticação."
            />
            <Card
              icon={<Server className="w-6 h-6 text-copaiba-700" />}
              title="4. Armazenamento redundante de arquivos"
              body="Documentos anexados (currículos do PSS, laudos do Setor Social, imagens de produtos, assinaturas digitais dos secretários) são gravados em Object Storage persistente — nunca são perdidos entre atualizações do site."
            />
            <Card
              icon={<Eye className="w-6 h-6 text-copaiba-700" />}
              title="5. Cabeçalhos de segurança HTTP"
              body="O site aplica cabeçalhos como HSTS, X-Content-Type-Options e políticas de referência que impedem sequestro de sessão, injeção de scripts (XSS) e ataques de MITM (Man-In-The-Middle)."
            />
            <Card
              icon={<Users className="w-6 h-6 text-copaiba-700" />}
              title="6. Papéis e permissões (RBAC)"
              body="Cada área tem seu perfil: Admin gerencia tudo, Secretaria acessa cooperados/documentos, Setor Social vê apenas solicitações sociais. Ninguém acessa dados fora do seu escopo."
            />
            <Card
              icon={<Cookie className="w-6 h-6 text-copaiba-700" />}
              title="7. LGPD — Privacidade de dados"
              body="Coletamos apenas os dados estritamente necessários (nome, contato, comunidade, quando aplicável CPF/RG para requerimentos). Os dados nunca são vendidos, cedidos ou utilizados para finalidades comerciais externas. O cooperado pode solicitar a exclusão a qualquer momento pelo Canal de Reclamações."
            />
          </div>
        </div>
      </section>

      {/* Como reconhecer que é seguro */}
      <section className="bg-forest-700 text-cream-100 noise-overlay">
        <div className="container-page py-16">
          <div className="max-w-4xl">
            <div className="text-xs uppercase tracking-[0.22em] text-copaiba-300 mb-3">Você mesmo pode conferir</div>
            <h2 className="font-display text-3xl md:text-4xl leading-tight">
              Sinais de que a conexão está segura
            </h2>
            <p className="mt-4 text-cream-100/80 leading-relaxed max-w-2xl">
              Ao navegar em <b>coomflona.com.br</b>, verifique estes indicadores no seu navegador:
            </p>
            <ul className="mt-6 space-y-4 text-cream-100/85 text-sm leading-relaxed max-w-2xl">
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-copaiba-500 text-forest-900 flex items-center justify-center font-bold shrink-0 mt-0.5">1</div>
                <div>
                  O endereço na barra começa com <b className="text-copaiba-300 font-mono">https://</b> (e não apenas
                  <span className="font-mono"> http://</span>). O <b>“s”</b> significa “seguro”.
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-copaiba-500 text-forest-900 flex items-center justify-center font-bold shrink-0 mt-0.5">2</div>
                <div>
                  Aparece um pequeno <b>cadeado 🔒 ao lado do endereço</b>. Clicando nele, o navegador mostra que o
                  certificado é válido.
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-copaiba-500 text-forest-900 flex items-center justify-center font-bold shrink-0 mt-0.5">3</div>
                <div>
                  O nome do domínio deve ser exatamente <b className="text-copaiba-300 font-mono">coomflona.com.br</b>
                  {" "}— sem letras trocadas, hifens estranhos ou domínios parecidos.
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-copaiba-500 text-forest-900 flex items-center justify-center font-bold shrink-0 mt-0.5">4</div>
                <div>
                  Nenhum aviso vermelho “conexão não segura” ou “site perigoso à frente” aparece no navegador ao acessar.
                </div>
              </li>
            </ul>
            <div className="mt-8 text-xs text-cream-100/50 border-t border-cream-100/10 pt-4 max-w-2xl">
              O selo de segurança exibido no rodapé é uma <b>indicação institucional</b> de que este site utiliza
              conexão HTTPS. Não representa qualquer tipo de certificação emitida por terceiros.
            </div>
          </div>
        </div>
      </section>

      {/* Contato de segurança */}
      <section className="container-page py-16">
        <div className="max-w-3xl bg-cream-200 border border-cream-400 p-8">
          <div className="flex items-start gap-4">
            <Mail className="w-6 h-6 text-forest-700 shrink-0 mt-1" />
            <div>
              <h3 className="font-display text-2xl text-forest-900">Encontrou algo suspeito?</h3>
              <p className="mt-3 text-forest-900/80 leading-relaxed">
                Se você identificou qualquer comportamento estranho, tentativa de golpe usando o nome da COOMFLONA ou
                falha de segurança neste site, entre em contato imediatamente. Levamos a sério toda notificação
                responsável.
              </p>
              <div className="mt-4 space-y-1 text-sm">
                <div><b className="text-forest-900">E-mail:</b> <a href="mailto:contato@coomflona.com.br" className="text-copaiba-700 hover:underline">contato@coomflona.com.br</a></div>
                <div><b className="text-forest-900">Canal de Reclamações:</b> <Link to="/canal-reclamacoes" className="text-copaiba-700 hover:underline">Formulário anônimo disponível</Link></div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function BadgeBig({ icon, title, sub }) {
  return (
    <div className="flex items-center gap-3 bg-forest-700/60 border border-copaiba-500/30 px-4 py-3 rounded-none">
      {icon}
      <div className="leading-tight">
        <div className="text-cream-100 font-medium">{title}</div>
        <div className="text-[11px] uppercase tracking-[0.15em] text-cream-100/60">{sub}</div>
      </div>
    </div>
  );
}

function Card({ icon, title, body }) {
  return (
    <div className="bg-white border border-cream-400 p-6">
      <div className="flex items-center gap-3 mb-3">
        {icon}
        <div className="font-display text-xl text-forest-900 leading-tight">{title}</div>
      </div>
      <div className="text-forest-900/80 text-sm leading-relaxed">{body}</div>
    </div>
  );
}
